import React from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { ButtonBase, Divider, IconButton, Toolbar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { LoadingTypography } from 'lib/LoadingTypography';
import { isString } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ApplicationState } from 'store/store';
import { PayAvatar } from './PayAvatar';
import { PayTableInfo } from './PayTableInfo';

const PREFIX = 'PayToolbar';

const classes = {
    title: `${PREFIX}title`,
    emptyButton: `${PREFIX}emptyButton`,
    iconButton: `${PREFIX}iconButton`,
    buttonBase: `${PREFIX}buttonBase`
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    width: '100%',
    margin: '0 auto',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: theme.spacing(1),
    [`& .${classes.title}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        fontWeight: 'bold',
        color: theme.palette.titleTextColour,
        height: '30px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        maxWidth: '80vw'
    },

    [`& .${classes.emptyButton}`]: {
        width: '24px',
        height: 1
    },

    [`& .${classes.iconButton}`]: {
        margin: theme.spacing(-1.5)
    },

    [`& .${classes.buttonBase}`]: {
        display: 'flex',
        flexDirection: 'column'
    }
}));

interface PayToolbarProps {
    table?: string | null;
    checkId?: string | null;
    onChangeClick?: () => void;
    disableBack?: boolean;
    onBack?: () => void;
    loading?: boolean;
    showMenu?: boolean;
    onMenuClick?: () => void;
    disableEdit?: boolean;
}

export const PayToolbar: React.FC<React.PropsWithChildren<PayToolbarProps>> = ({
    table,
    checkId,
    disableBack,
    onBack,
    onChangeClick,
    showMenu,
    onMenuClick,
    disableEdit,
    loading
}) => {
    const { navigate } = useLocalHistory();
    const { currentLocation, isCurrentLocationLoading } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const handleLeftClick = React.useCallback(() => {
        if (typeof onBack === 'function') {
            onBack();
        } else {
            navigate(-1);
        }
    }, [onBack, navigate]);
    const handleChangeClick = React.useCallback(() => {
        if (!disableEdit && onChangeClick) {
            onChangeClick();
        }
    }, [disableEdit, onChangeClick]);
    const isLoading = loading || isCurrentLocationLoading;
    return (
        <>
            <StyledToolbar disableGutters variant="dense">
                {!disableBack && (
                    <IconButton onClick={handleLeftClick} className={classes.iconButton} size="large">
                        <ArrowBackIcon color="inherit" />
                    </IconButton>
                )}
                {disableBack &&
                    (showMenu && !!onMenuClick ? (
                        <IconButton onClick={onMenuClick} className={classes.iconButton} size="large">
                            <MenuIcon color="inherit" />
                        </IconButton>
                    ) : (
                        <div className={classes.emptyButton} />
                    ))}
                <ButtonBase
                    className={classes.buttonBase}
                    disableRipple={disableEdit}
                    onClick={handleChangeClick}
                >
                    <LoadingTypography
                        loading={isLoading}
                        align="center"
                        variant="h6"
                        className={classes.title}
                        width={200}
                    >
                        {currentLocation?.title}
                    </LoadingTypography>
                    {(isString(table) || isString(checkId)) && (
                        <PayTableInfo
                            isLoading={isLoading}
                            checkId={checkId}
                            table={table}
                            disableEdit={disableEdit}
                        />
                    )}
                </ButtonBase>
                <PayAvatar className={classes.iconButton} />
            </StyledToolbar>
            <Divider />
        </>
    );
};
